import { PathsV1RuleUuidGetParametersQueryExtend } from "@/api-schema-enums";
import { useRuleByUuidQuery } from "@/common/api/rule/use-rule-by-uuid.query";
import { useRuleUpdateMutation } from "@/common/api/rule/use-rule-update.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { useUserRuleAttributes } from "@/common/hooks/use-user-rule-attributes";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Loader, LoaderSize } from "@/components/ui/loader";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { RuleEditorPanels, useRuleUserPreview } from "./_components/rule-affected-users-preview";
import { RuleConfigurationHintDialog } from "./_components/rule-configuration-hint-dialog";
import { RuleFormFields, formSchema, type formSchemaType } from "./_components/rule-form-fields";

export function RuleUpdateView() {
  const { t } = useTranslation(["ram", "common"]);
  const [_, navigate] = useLocation();
  const params = useParams<{ groupUuid: string; ruleUuid: string }>();

  const ruleByUuidQueryOptions = useMemo(
    () => ({
      parameters: {
        extend: [
          PathsV1RuleUuidGetParametersQueryExtend.Companies,
          PathsV1RuleUuidGetParametersQueryExtend.CompanyGroups,
          PathsV1RuleUuidGetParametersQueryExtend.Branches,
          PathsV1RuleUuidGetParametersQueryExtend.Departments,
          PathsV1RuleUuidGetParametersQueryExtend.Occupations,
          PathsV1RuleUuidGetParametersQueryExtend.Brands,
          PathsV1RuleUuidGetParametersQueryExtend.JobFunctions,
        ],
      },
    }),
    [],
  );

  const { data: ruleByUuidQueryData, isPending: ruleByUuidQueryIsLoading } = useRuleByUuidQuery(
    params.ruleUuid,
    ruleByUuidQueryOptions,
  );
  const { isPending: ruleUpdateMutationIsPending, mutateAsync: ruleUpdateMutationMutateAsync } =
    useRuleUpdateMutation();

  const {
    companies,
    companyGroups,
    branches,
    occupations,
    departments,
    brands,
    jobFunctions,
    isPending: userRuleAttributesIsPending,
  } = useUserRuleAttributes();
  const pageTitle = t("ram:EditRule");
  const pageSubtitle = t("ram:SelectCriteriaToSeePreviewOfAffectedUsers");
  const isPageLoading = userRuleAttributesIsPending || ruleByUuidQueryIsLoading;

  const form = useForm<formSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companies: [],
      companyGroups: [],
      branches: [],
      occupations: [],
      departments: [],
      brands: [],
      jobFunctions: [],
    },
  });

  const formInitializedRef = useRef(false);

  useEffect(() => {
    if (!ruleByUuidQueryData || formInitializedRef.current) {
      return;
    }

    form.reset(
      {
        companies: ruleByUuidQueryData.companies?.map((item) => item.uuid) ?? [],
        companyGroups: ruleByUuidQueryData.companyGroups?.map((item) => item.uuid) ?? [],
        branches: ruleByUuidQueryData.branches?.map((item) => item.uuid) ?? [],
        departments: ruleByUuidQueryData.departments?.map((item) => item.uuid) ?? [],
        occupations: ruleByUuidQueryData.occupations?.map((item) => item.uuid) ?? [],
        brands: ruleByUuidQueryData.brands?.map((item) => item.uuid) ?? [],
        jobFunctions: ruleByUuidQueryData.jobFunctions?.map((item) => item.uuid) ?? [],
      },
      { keepDirtyValues: true },
    );
    formInitializedRef.current = true;
  }, [ruleByUuidQueryData, form]);

  const { hasAnySelection, userPreviewBody, affectedUsersCount } = useRuleUserPreview(form.control);

  const onSubmit = async (values: formSchemaType) => {
    await toastMutationStatus({
      mutationFn: () =>
        ruleUpdateMutationMutateAsync({ ...values, groupUuid: params.groupUuid, uuid: params.ruleUuid }),
      successMessage: t("ram:RuleHasBeenUpdated"),
      errorMessage: t("ram:ErrorUpdatingTheRule"),
    });

    navigate(`~/ram/group/${params.groupUuid}`);
  };

  if (isPageLoading) {
    return (
      <CenteredLayout variant={CenteredLayoutVariant.Default} title={pageTitle} subTitle={pageSubtitle}>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loader size={LoaderSize.Large} />
        </div>
      </CenteredLayout>
    );
  }

  return (
    <CenteredLayout variant={CenteredLayoutVariant.Default} title={pageTitle} subTitle={pageSubtitle}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <RuleEditorPanels
            configurationAction={<RuleConfigurationHintDialog />}
            configurationTitle={t("ram:RuleConfiguration")}
            hasAnySelection={hasAnySelection}
            previewTitle={t("ram:AffectedUsersCount", { count: affectedUsersCount })}
            userPreviewBody={userPreviewBody}
          >
            <RuleFormFields
              isLoading={ruleUpdateMutationIsPending || isPageLoading}
              formControl={form.control}
              companies={companies?.data}
              companyGroups={companyGroups?.data}
              branches={branches?.data}
              occupations={occupations?.data}
              departments={departments?.data}
              brands={brands?.data}
              jobFunctions={jobFunctions?.data}
            />
          </RuleEditorPanels>

          <div className="flex flex-wrap justify-end gap-4">
            <Button
              disabled={ruleUpdateMutationIsPending}
              onClick={() => navigate(`~/ram/group/${params.groupUuid}`)}
              type="button"
              variant={ButtonVariant.Outlined}
              colorVariant={ButtonColorVariant.Secondary}
            >
              {t("common:Cancel")}
            </Button>
            <Button disabled={ruleUpdateMutationIsPending || !form.formState.isValid} type="submit">
              {t("common:Save")}
            </Button>
          </div>
        </form>
      </Form>
    </CenteredLayout>
  );
}
